;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/manage/billing/:tab*\",\"regex\":\"^/manage/billing/(.+)$\",\"paramNames\":[\"tab\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/analytics\"},{\"path\":\"/dashboard\"},{\"path\":\"/deploy\"},{\"path\":\"/deploy/keys\"},{\"path\":\"/deploy/models\"},{\"path\":\"/keys\"},{\"path\":\"/manage\"},{\"path\":\"/manage/account\"},{\"path\":\"/manage/billing\"},{\"path\":\"/manage/members\"},{\"path\":\"/manage/org\"},{\"path\":\"/manage/projects\"},{\"path\":\"/manage/settings\"},{\"path\":\"/models\"},{\"path\":\"/usage\"},{\"path\":\"/checkout/success\"},{\"path\":\"/login\"},{\"path\":\"/logout\"},{\"path\":\"/onboarding\"},{\"path\":\"/onboarding/organization\"},{\"path\":\"/signup\"},{\"path\":\"/verify-email\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.3.8";import type { ReplayEvent } from "@sentry/core";
import * as Sentry from "@sentry/nextjs";
import {
  beforeSendReplay,
  getSharedSentryOptions,
  isSentryReplayBlockedPath,
  parseSampleRate,
} from "@/lib/sentry/config";

const replaySessionSampleRate = parseSampleRate(process.env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0.01);
const replayErrorSampleRate = parseSampleRate(process.env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1.0);

const pathnameFromHref = (href: string): string => {
  try {
    return new URL(href, window.location.origin).pathname;
  } catch {
    return href.split("?")[0]?.split("#")[0] ?? href;
  }
};

const startReplayForSampleRates = (replay: NonNullable<ReturnType<typeof Sentry.getReplay>>) => {
  if (Math.random() < replaySessionSampleRate) {
    replay.start();
    return;
  }

  if (Math.random() < replayErrorSampleRate) {
    replay.startBuffering();
  }
};

const syncReplayForPath = (pathname: string) => {
  const replay = Sentry.getReplay();
  if (!replay) {
    return;
  }

  // rrweb Meta snapshots store window.location.href and cannot be scrubbed.
  // Drop the pending segment so an auth URL never leaves the browser.
  if (isSentryReplayBlockedPath(pathname)) {
    void replay.stop({ flush: false });
    return;
  }

  if (replay.getReplayId()) {
    return;
  }

  startReplayForSampleRates(replay);
};

const replayBlockedAtInit = isSentryReplayBlockedPath(window.location.pathname);

Sentry.init({
  ...getSharedSentryOptions(),
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      beforeErrorSampling: () => !isSentryReplayBlockedPath(window.location.pathname),
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.consoleLoggingIntegration(),
    Sentry.featureFlagsIntegration(),
  ],
  replaysSessionSampleRate: replayBlockedAtInit ? 0 : replaySessionSampleRate,
  replaysOnErrorSampleRate: replayBlockedAtInit ? 0 : replayErrorSampleRate,
  profileSessionSampleRate: parseSampleRate(
    process.env.NEXT_PUBLIC_SENTRY_PROFILES_SAMPLE_RATE ?? process.env.SENTRY_PROFILES_SAMPLE_RATE,
    0.1,
  ),
});

Sentry.addEventProcessor((event, hint) => {
  if (event.type !== "replay_event") {
    return event;
  }

  return beforeSendReplay(event as ReplayEvent, hint);
});

syncReplayForPath(window.location.pathname);

export const onRouterTransitionStart = (href: string, navigationType: string) => {
  syncReplayForPath(pathnameFromHref(href));
  Sentry.captureRouterTransitionStart(href, navigationType);
};
